import { format } from 'date-fns';
import { useId } from 'react';
import { useTranslations } from '@/i18n';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import type { CityFormData } from '@/components/trips/form/CityForm';

interface CityCardProps {
  city: CityFormData;
  onEdit: () => void;
  onDelete: () => void;
  isDeleting?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  tripType?: 'local' | 'international';
  /** Whether arrival date has validation issue */
  hasArrivalDateIssue?: boolean;
  /** Whether departure date has validation issue */
  hasDepartureDateIssue?: boolean;
}

export default function CityCard({
  city,
  onEdit,
  onDelete,
  isDeleting = false,
  canEdit = true,
  canDelete = true,
  tripType,
  hasArrivalDateIssue = false,
  hasDepartureDateIssue = false,
}: CityCardProps) {
  const t = useTranslations('cityForm');
  const accommodationCheckboxId = useId();
  const transportCheckboxId = useId();
  const fuelCheckboxId = useId();
  const allowFuel = tripType !== 'international';

  return (
    <div className="relative box-border flex w-full flex-col items-end justify-start rounded-3xl bg-white p-5 shadow-[0px_8px_24px_-8px_rgba(31,31,31,0.08)] lg:w-[36.375rem] lg:shrink-0">
      <div className="relative flex w-full shrink-0 flex-col items-start justify-start gap-4">
        {/* Header with Edit and Delete Buttons */}
        <div className="relative flex w-full shrink-0 items-center justify-between">
          <div className="relative flex h-8 shrink-0 flex-col justify-center text-[18px] leading-[0] font-medium text-black">
            <p className="leading-7">{t('cityDetails')}</p>
          </div>
          <div className="flex gap-2">
            {canEdit && (
              <Button
                onClick={onEdit}
                variant="ghost"
                size="none"
                className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#f1f1f1] bg-white p-2 hover:bg-[#f9f9f9]"
              >
                <img src="/assets/icons/edit.svg" alt="" className="h-5 w-5" />
              </Button>
            )}
            {canDelete && (
              <Button
                onClick={onDelete}
                variant="ghost"
                size="none"
                className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#f1f1f1] bg-white p-2 hover:bg-[#ffeded]"
                disabled={isDeleting}
              >
                <img src="/assets/icons/delete.svg" alt="" className="h-5 w-5" />
              </Button>
            )}
          </div>
        </div>

        {/* City Details */}
        <div className="relative flex w-full shrink-0 flex-col items-start justify-start gap-4">
          <div className="relative flex w-full shrink-0 flex-col items-start justify-start gap-8">
            <div className="relative box-border flex w-full shrink-0 flex-col items-start justify-start rounded-2xl bg-white px-0 py-2">
              {/* Country and City Row */}
              <div className="relative flex w-full shrink-0 items-start justify-start">
                <div className="relative box-border flex min-h-px min-w-px shrink-0 grow basis-0 flex-col items-start justify-start gap-1 px-0 py-3">
                  <div className="relative w-full shrink-0 text-sm text-[#666666]">
                    <p className="leading-5">{t('selectCountry')}</p>
                  </div>
                  <div className="relative w-full shrink-0 text-base font-medium text-black">
                    <p className="leading-6">{city.countryName}</p>
                  </div>
                </div>
                <div className="relative box-border flex min-h-px min-w-px shrink-0 grow basis-0 flex-col items-start justify-start gap-1 px-0 py-3">
                  <div className="relative w-full shrink-0 text-sm text-[#666666]">
                    <p className="leading-5">{t('selectCity')}</p>
                  </div>
                  <div className="relative w-full shrink-0 text-base font-medium text-black">
                    <p className="leading-6">{city.cityName}</p>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="relative my-2 h-px w-full shrink-0 bg-[#f0f0f0]" />

              {/* Dates Row */}
              <div className="relative flex w-full shrink-0 items-start justify-start">
                <div className="relative box-border flex min-h-px min-w-px shrink-0 grow basis-0 flex-col items-start justify-start gap-1 px-0 py-3">
                  <div
                    className={cn(
                      'relative w-full shrink-0 text-sm',
                      hasArrivalDateIssue ? 'text-[#e5484d]' : 'text-[#666666]'
                    )}
                  >
                    <p className="leading-5">{t('arrivalDate')}</p>
                  </div>
                  <div
                    className={cn(
                      'relative w-full shrink-0 text-base font-medium',
                      hasArrivalDateIssue ? 'text-[#e5484d]' : 'text-black'
                    )}
                  >
                    <p className="leading-6">{format(city.arrivalDate, 'dd/MM/yyyy')}</p>
                  </div>
                </div>
                <div className="relative box-border flex min-h-px min-w-px shrink-0 grow basis-0 flex-col items-start justify-start gap-1 px-0 py-3">
                  <div
                    className={cn(
                      'relative w-full shrink-0 text-sm',
                      hasDepartureDateIssue ? 'text-[#e5484d]' : 'text-[#666666]'
                    )}
                  >
                    <p className="leading-5">{t('departureDate')}</p>
                  </div>
                  <div
                    className={cn(
                      'relative w-full shrink-0 text-base font-medium',
                      hasDepartureDateIssue ? 'text-[#e5484d]' : 'text-black'
                    )}
                  >
                    <p className="leading-6">{format(city.departureDate, 'dd/MM/yyyy')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Checkboxes */}
          <div className="relative flex w-full shrink-0 flex-col items-start justify-start gap-2">
            <div className="relative flex w-full shrink-0 flex-col items-start justify-start gap-2">
              {/* Accommodation Checkbox */}
              <div className="relative flex min-h-[2.5rem] w-full items-center justify-start gap-1.5">
                <Checkbox
                  id={accommodationCheckboxId}
                  checked={city.needsAccommodation}
                  disabled
                  className="h-5 w-5 data-[state=checked]:border-[#DADADA] data-[state=checked]:bg-[#DADADA] data-[state=unchecked]:border-[#DADADA] data-[state=unchecked]:bg-white"
                />
                <label
                  htmlFor={accommodationCheckboxId}
                  className="cursor-pointer text-[15px] leading-6 text-[#292929]"
                >
                  {t('needsAccommodation')}
                </label>
              </div>

              {/* Transport Checkbox */}
              <div className="relative flex min-h-[2.5rem] w-full items-center justify-start gap-1.5">
                <Checkbox
                  id={transportCheckboxId}
                  checked={city.needsTransport}
                  disabled
                  className="h-5 w-5 data-[state=checked]:border-[#DADADA] data-[state=checked]:bg-[#DADADA] data-[state=unchecked]:border-[#DADADA] data-[state=unchecked]:bg-white"
                />
                <label
                  htmlFor={transportCheckboxId}
                  className="cursor-pointer text-[15px] leading-6 text-[#292929]"
                >
                  {t('needsTransport')}
                </label>
              </div>

              {/* Fuel Checkbox - only show for non-international trips */}
              {allowFuel && (
                <div className="relative flex min-h-[2.5rem] w-full items-center justify-start gap-1.5">
                  <Checkbox
                    id={fuelCheckboxId}
                    checked={city.needsFuel}
                    disabled
                    className="h-5 w-5 data-[state=checked]:border-[#DADADA] data-[state=checked]:bg-[#DADADA] data-[state=unchecked]:border-[#DADADA] data-[state=unchecked]:bg-white"
                  />
                  <label
                    htmlFor={fuelCheckboxId}
                    className="cursor-pointer text-[15px] leading-6 text-[#292929]"
                  >
                    {t('needsFuel')}
                  </label>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
